import update from 'immutability-helper';
import { useDrag, useDrop } from 'react-dnd';
import type { ColumnType } from 'antd/es/table';
import { Table, Empty, Button } from 'design-system';
import {
  DeleteOutlined
} from '@ant-design/icons';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { getSubjectDisplayName } from 'src/utils/subjectUtils';

import { Subject, TypeCode } from 'src/services/subject';
import { AssessmentDimension } from 'src/services/assessmentDimension';

import './tableTemplates.css';

// KPI专用表头（不包含操作列）
export type KPIRow = {
  index: number;
  subject: string;
  dynamicMonths: string;
  nextMonth: string;
  sum: string;
  sumDiff: string;
  sumRate: string;
  backgroundColor: string;
  originalItem: ExtendedSubject;
};

export type NormalRow = {
  index: number;
  subject: ExtendedSubject;
  past: string;
  current: string;
  difference: string;
  object1: string;
  object2: string;
  object3: string;
  object: string;
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  month1: string;
  month2: string;
  month3: string;
  week1: string;
  week2: string;
  week3: string;
  week4: string;
  week5: string;
  achievement: string;
  person: string;
  backgroundColor: string;
  assessmentDimension: string;
  target?: string;
  targetAmoebaPercentage?: string;
  targetCompanyPercentage?: string;
  currentAmoebaPercentage?: string;
  currentCompanyPercentage?: string;
  differenceAmoebaPercentage?: string;
  differenceCompanyPercentage?: string;
  comparisonRatio?: string;
  comparisonTrend?: string;
  currentRatio1?: string;
  currentRatio2?: string;
  achievementRatio1?: string;
  achievementRatio2?: string;
};

export interface ColumnSetting {
  title: string;
  subTitle?: string;
  key: string;
  order?: number;
  width?: number;
  dataIndex?: string;
}
let currentColumnSettings: ColumnSetting[] = [];
export const setCurrentColumnSettings = (settings: ColumnSetting[] | null | undefined) => {
  if (!settings) {
    currentColumnSettings = [];
    return;
  }
  try {
    const validSettings = settings.filter(col => {
      const isValid = col && typeof col === 'object' && col.key && typeof col.key === 'string';
      return isValid;
    });
    currentColumnSettings = validSettings;
  } catch (error) {
          // 错误处理
    currentColumnSettings = [];
  }
};
export interface ExtendedSubject extends Omit<Subject, 'assessmentDimension'> {
  parentName?: string;
  grandParentName?: string;
  assessmentDimension?: AssessmentDimension;
  createdAt: string;
  updatedAt: string;
  _originalDragId?: string;
  _originalDragName?: string;
  _dragTime?: string;
  _dragName?: string;
  _dragFullName?: string;
  _originalIndex?: number;
  typeCode?: keyof typeof TypeCode;
  code?: string;
  type?: string;
}
interface TableProps {
  canvasItems: ExtendedSubject[];
  selectedRowId?: string | null;
  rowBackgroundColors?: Map<string, string>;
  onRowSelect?: (rowId: string) => void;
  readonly?: boolean; 
  columnSettings?: ColumnSetting[]; 
  onTableDataChange?: (data: TableData) => void;
}
export interface TableHeaderItem {
  title: string;
  subTitle?: string | null;
  key: string;
  render?: (value: unknown, record: NormalRow | KPIRow, index: number) => React.ReactNode;
}
export type TableHeaderArray = TableHeaderItem[];
export interface TableData {
  header: TableHeaderArray;
  rows: (KPIRow | NormalRow)[];
}
const type = 'DraggableRow';
interface DraggableRowProps {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLTableRowElement>;
  readonly?: boolean; 
}
const DraggableRow = ({ 
  index, 
  moveRow, 
  className = '', 
  style = {}, 
  children,
  onClick,
  readonly = false 
}: DraggableRowProps) => {
  const ref = React.useRef<HTMLTableRowElement>(null);
  const [isDraggingLocal, setIsDraggingLocal] = useState(false);
  const mouseDownRef = useRef({
    x: 0,
    y: 0,
    time: 0
  });
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? 'drop-over-downward' : 'drop-over-upward',
      };
    },
    drop: (item: { index: number }) => {
      if (item.index !== index) {
        moveRow(item.index, index);
      }
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => {
      const dragging = monitor.isDragging();
      setIsDraggingLocal(dragging);
      return {
        isDragging: dragging,
      };
    },
  });
  if (!readonly) {
    drop(drag(ref));
  }
  if (readonly) {
    return (
      <tr
        ref={ref}
        className={className}
        style={{ ...style }}
        onClick={onClick}
      >
        {children}
      </tr>
    );
  }
  const isActive = isOver && dropClassName;
  const trClassName = `${className}${isActive ? ` ${dropClassName}` : ''}${isDragging ? ' dragging' : ''}`;
  const handleMouseDown = (e: React.MouseEvent<HTMLTableRowElement>) => {
    mouseDownRef.current = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now()
    };
  };
  const handleClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    if (onClick) {
      onClick(e);
    }
  };
  return (
    <tr
      ref={ref}
      className={trClassName}
      style={{ ...style, cursor: isDragging ? 'move' : 'pointer' }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      {children}
    </tr>
  );
};
export const generateSharedAntdColumns = (tableHeader: TableHeaderArray) => {
  const groupedHeaders: Record<string, TableHeaderItem[]> = {};
  const yearlyColumns = tableHeader.filter(header => 
    header.key.startsWith('q') && ['q1', 'q2', 'q3', 'q4'].includes(header.key)
  );
  if (yearlyColumns.length > 0) {
    const yearlyTitle = '年度计划(元)';
    groupedHeaders[yearlyTitle] = yearlyColumns;
    yearlyColumns.forEach(col => {
      if (!col.subTitle) {
        col.subTitle = col.key.toUpperCase();
      }
    });
  }
  const weeklyColumns = tableHeader.filter(header => 
    header.key.startsWith('week') && ['week1', 'week2', 'week3', 'week4', 'week5'].includes(header.key)
  );
  if (weeklyColumns.length > 0) { 
    const monthlyTitle = '月度计划(元)';
    groupedHeaders[monthlyTitle] = weeklyColumns;
    weeklyColumns.forEach(col => {
      if (!col.subTitle) {
        col.subTitle = `第${col.key.slice(-1)}周`;
      }
    });
  }
  const quarterlyColumns = tableHeader.filter(header => 
    header.key.startsWith('month') && ['month1', 'month2', 'month3'].includes(header.key)
  );
  if (quarterlyColumns.length > 0) {
    const quarterlyTitle = '季度计划(元)';
    groupedHeaders[quarterlyTitle] = quarterlyColumns;
    quarterlyColumns.forEach(col => {
      if (!col.subTitle) {
        col.subTitle = `x月份`;
      }
    });
  }
  tableHeader.forEach(header => {
    if (!['q1', 'q2', 'q3', 'q4', 'week1', 'week2', 'week3', 'week4', 'week5', 'month1', 'month2', 'month3'].includes(header.key) && header.subTitle) {
      if (!groupedHeaders[header.title]) {
        groupedHeaders[header.title] = [];
      }
      groupedHeaders[header.title].push(header);
    }
  });
  
  // 创建键到索引的映射以保留原始顺序
  const keyToIndexMap = new Map<string, number>();
  tableHeader.forEach((header, index) => {
    keyToIndexMap.set(header.key, index);
  });

  const allTitles = new Set<string>();
  const titleOrderMap = new Map<string, number>();
  tableHeader.forEach((header, index) => {
    if (['q1', 'q2', 'q3', 'q4'].includes(header.key)) {
      if (!titleOrderMap.has(header.title)) {
        titleOrderMap.set(header.title, index);
      }
    } else {
      titleOrderMap.set(header.key, index);
    }
    allTitles.add(header.title);
  });
  
  const columns: Array<
    | {
        title: string;
        dataIndex?: string;
        key: string;
        width?: number;
        order?: number;
        originalIndex?: number;
        render?: (value: unknown, record: NormalRow | KPIRow, index: number) => React.ReactNode;
        children?: {
          title: string | null;
          dataIndex: string;
          key: string;
          width?: number;
        }[];
      }
  > = [];
  
  const indexHeaderItem = tableHeader.find(header => header.key === 'index');
  if (indexHeaderItem) {
    columns.push({
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      order: -1,
      originalIndex: keyToIndexMap.get('index') || -1,
      render: (value: unknown) => (<div className="first-column-cell">{String(value)}</div>)
    });
  }
  
  const baseHeaders = tableHeader.filter(header => 
    header.key !== 'index' && 
    !header.subTitle && 
    !Object.keys(groupedHeaders).includes(header.title) &&
    !['q1', 'q2', 'q3', 'q4'].includes(header.key) &&
    !['week1', 'week2', 'week3', 'week4', 'week5'].includes(header.key)
  );
  
  baseHeaders.forEach(header => {
    const column: {
      title: string;
      dataIndex: string;
      key: string;
      width?: number;
      order?: number;
      originalIndex?: number;
      render?: (value: unknown, record: NormalRow | KPIRow, index: number) => React.ReactNode;
    } = {
      title: header.title,
      dataIndex: header.key,
      key: header.key,
      width: 100,
      order: titleOrderMap.get(header.key) || 999,
      originalIndex: keyToIndexMap.get(header.key) 
    };
    
    if (header.key === 'assessmentDimension' || header.key === 'dimension') {
      column.render = (_value: unknown, record: NormalRow | KPIRow) => {
        let dimensionName = '未分配考核维度';
        if ('originalItem' in record && record.originalItem && record.originalItem.assessmentDimension && record.originalItem.assessmentDimension.name) {
          dimensionName = record.originalItem.assessmentDimension.name;
        } else if ('subject' in record && record.subject && typeof record.subject === 'object' && record.subject.assessmentDimension && record.subject.assessmentDimension.name) {
          dimensionName = record.subject.assessmentDimension.name;
        } else if ('assessmentDimension' in record && record.assessmentDimension && record.assessmentDimension !== '--' && record.assessmentDimension !== '未分配考核维度') {
          dimensionName = record.assessmentDimension as string;
        } else if ('dimension' in record && record.dimension && record.dimension !== '--' && record.dimension !== '未分配考核维度') {
          dimensionName = record.dimension as string;
        }
        return <span>{dimensionName}</span>;
      };
    }
    
    if (header.key === 'person' || header.key === 'responsible') {
      column.render = (_value: unknown, record: NormalRow | KPIRow) => {
        const value = (record as Record<string, unknown>)[header.key];
        return <span>{typeof value === 'string' && value ? String(value) : '--'}</span>;
      };
    }
    
    columns.push(column);
  });
  
  Object.entries(groupedHeaders).forEach(([title, headers]) => {
    const columnKeys = headers.map(h => h.key);
    const minIndex = Math.min(...columnKeys.map(key => keyToIndexMap.get(key) || 999));
    
    columns.push({
      title,
      key: `group-${title}`,
      order: titleOrderMap.get(title) || 999,
      originalIndex: minIndex,
      children: headers.map(header => ({
        title: header.subTitle ?? null,
        dataIndex: header.key,
        key: header.key,
        width: 100
      }))
    });
  });
  
  // 根据原始的tableHeader顺序排序列
  columns.sort((a, b) => {
    const aIndex = a.originalIndex !== undefined ? a.originalIndex : 999;
    const bIndex = b.originalIndex !== undefined ? b.originalIndex : 999;
    return aIndex - bIndex;
  });
  
  const finalColumns = [...columns];
  
  return finalColumns;
};
export const KPI_HEADER: ColumnSetting[] = [
  { key: 'index', title: '序号', dataIndex: 'index', order: 0 },
  { key: 'subject', title: '考核科目', dataIndex: 'subject', order: 1 },
  { key: 'dynamicMonths', title: '月份区间', dataIndex: 'dynamicMonths', order: 2 },
  { key: 'nextMonth', title: '下一个月', dataIndex: 'nextMonth', order: 3 },
  { key: 'sum', title: '合计', dataIndex: 'sum', order: 4 },
  { key: 'sumDiff', title: '合计差异', dataIndex: 'sumDiff', order: 5 },
  { key: 'sumRate', title: '合计完成率', dataIndex: 'sumRate', order: 6 },
];

export const createTableHeaderFromSettings = (
  rowItems: ExtendedSubject[] = [],
  columnSettings?: ColumnSetting[],
  templateType?: string
): TableHeaderItem[] => {
  if (templateType && templateType.toLowerCase() === 'kpi') {
    return KPI_HEADER.map(setting => ({
      key: setting.key,
      title: setting.title,
      subTitle: null,
    }));
  }
  const baseHeaders: TableHeaderItem[] = [
    { key: 'index', title: '序号', subTitle: null },
    { key: 'dimension', title: '考核维度', subTitle: null },
    { key: 'subject1', title: '一级科目', subTitle: null },
    { key: 'subject2', title: '二级科目', subTitle: null },
    { key: 'subject3', title: '三级科目', subTitle: null },
    { key: 'previousActual', title: '往期实际', subTitle: null },
    { key: 'currentTarget', title: '本期目标', subTitle: null },
    { key: 'comparison', title: '对比差距', subTitle: null },
    { key: 'achievement', title: '实际达成情况', subTitle: null },
    { key: 'responsible', title: '负责人', subTitle: null },
    { key: 'action', title: '操作', subTitle: null },
  ];
  let finalHeaders = [...baseHeaders];
  if (columnSettings && columnSettings.length > 0) {
    finalHeaders = columnSettings.map(setting => ({
      key: setting.key,
      title: setting.title,
      subTitle: setting.subTitle || null,
    }));
  }
  return finalHeaders;
};

export const createNewTableRows = (
  items: ExtendedSubject[] = [],
  rowBackgroundColors?: Map<string, string>,
  templateType?: string,
  allSubjects?: ExtendedSubject[]
): (KPIRow | NormalRow)[] => {
  if (templateType && templateType.toLowerCase() === 'kpi') {
    const rows: KPIRow[] = items.map((item, index) => {
      // 使用 getSubjectDisplayName 函数生成完整的科目显示名称
      let subjectDisplayName: string;
      try {
        if (allSubjects && allSubjects.length > 0) {
          const fullSubject = allSubjects.find(s => s.id === item.id);
          if (fullSubject) {
            subjectDisplayName = getSubjectDisplayName(fullSubject as Subject, allSubjects as Subject[], '/');
          } else {
            subjectDisplayName = getSubjectDisplayName(item as Subject, allSubjects as Subject[], '/');
          }
        } else {
          subjectDisplayName = item.name;
          if (item.parentName && item.grandParentName) {
            subjectDisplayName = `${item.grandParentName}/${item.parentName}/${item.name}`;
          } else if (item.parentName) {
            subjectDisplayName = `${item.parentName}/${item.name}`;
          }
          if (item.code) {
            subjectDisplayName = `${subjectDisplayName} (${item.code})`;
          }
        }
      } catch (error) {
        subjectDisplayName = item.name;
        if (item.code) {
          subjectDisplayName = `${subjectDisplayName} (${item.code})`;
        }
      }
      return {
        index: index + 1,
        subject: subjectDisplayName,
        dynamicMonths: '-',
        nextMonth: '-',
        sum: '-',
        sumDiff: '-',
        sumRate: '-',
        backgroundColor: rowBackgroundColors?.get(item.id) || '#fff',
        originalItem: item,
      };
    });
    return rows;
  }
  const rows = items.map((item, index) => {
    const getNameWithCode = <T extends { code?: string }>(name: string, item: T) => {
      const code = item.code || '';
      return code ? `${name} (${code})` : name;
    };
    const dimensionName = item.assessmentDimension?.name || '未分配考核维度';
    let subject1 = '--';
    let subject2 = '--';
    let subject3 = '--';
    let object1 = '--';
    let object2 = '--';
    let object3 = '--';
    if (item.parentName && item.grandParentName) {
      subject1 = item.grandParentName || '--';
      subject2 = item.parentName || '--';
      subject3 = item.name || '--';
      object1 = item.grandParentName || '--';
      object2 = item.parentName || '--';
      object3 = item.name || '--';
    } else if (item.parentName) {
      subject1 = item.parentName || '--';
      subject2 = item.name || '--';
      subject3 = '--';
      object1 = item.parentName || '--';
      object2 = item.name || '--';
      object3 = '--';
    } else {
      subject1 = item.name || '--';
      subject2 = '--';
      subject3 = '--';
      object1 = item.name || '--';
      object2 = '--';
      object3 = '--';
    }
    let objectText = '';
    if (item.parentName && item.grandParentName) {
      objectText = `${item.grandParentName} > ${item.parentName} > ${item.name}`;
    } else if (item.parentName) {
      objectText = `${item.parentName} > ${item.name}`;
    } else {
      objectText = item.name;
    }
    return {
      index: index + 1,
      subject: item,
      object1,
      object2,
      object3,
      object: objectText || '--',
      past: '--',
      current: '--',
      difference: '--',
      q1: '--',
      q2: '--',
      q3: '--',
      q4: '--',
      month1: '--',
      month2: '--',
      month3: '--',
      week1: '--',
      week2: '--',
      week3: '--',
      week4: '--',
      week5: '--',
      achievement: '--',
      person: '--',
      backgroundColor: rowBackgroundColors?.get(item.id) || '#fff',
      assessmentDimension: dimensionName,
      target: '--',
      targetAmoebaPercentage: '--',
      targetCompanyPercentage: '--',
      currentAmoebaPercentage: '--',
      currentCompanyPercentage: '--',
      differenceAmoebaPercentage: '--',
      differenceCompanyPercentage: '--',
      comparisonRatio: '--',
      comparisonTrend: '--',
      currentRatio1: '--',
      currentRatio2: '--',
      achievementRatio1: '--',
      achievementRatio2: '--',
    };
  });
  return rows;
};
export const YearlyPlanTable = (props: {
  canvasItems: ExtendedSubject[];
  selectedRowId?: string | null;
  rowBackgroundColors?: Map<string, string>;
  onRowSelect?: (rowId: string) => void;
  readonly?: boolean;
  columnSettings?: ColumnSetting[];
  onRemoveItem?: (itemId: string) => void;
  onTableDataChange?: (data: TableData) => void;
}) => {
  const { canvasItems, selectedRowId, rowBackgroundColors, onRowSelect, readonly = false, columnSettings, onRemoveItem, onTableDataChange } = props;
  
  const initialData = createNewTableRows(canvasItems || [], rowBackgroundColors);
  
  // 状态
  const [data, setData] = useState<(KPIRow | NormalRow)[]>(initialData);
  const [tableData, setTableData] = useState<TableData>({
    header: createTableHeaderFromSettings(canvasItems, columnSettings),
    rows: initialData
  });

  // 生成列配置
  const columns = useMemo(() => {
    // 获取表头
    const tableHeader = createTableHeaderFromSettings(canvasItems, columnSettings);
    // 转换为表格列配置
    const baseColumns = generateSharedAntdColumns(tableHeader);
    
    // 添加操作列 - 仅当非只读模式且提供了删除回调时显示
    if (!readonly && onRemoveItem) {
      baseColumns.push({
        title: '操作',
        dataIndex: 'action',
        key: 'action',
        width: 60,
        render: (_: unknown, record: NormalRow | KPIRow) => (
          <Button
            type="text"
            danger
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onRemoveItem) onRemoveItem((record as KPIRow).originalItem?.id ?? (record as NormalRow).subject?.id);
            }}
            icon={<DeleteOutlined />}
          />
        ),
      });
    } 
    
    return baseColumns;
  }, [canvasItems, columnSettings, onRemoveItem, readonly]);

  useEffect(() => {
    const newDataSource = createNewTableRows(canvasItems || [], rowBackgroundColors);
    setData(newDataSource);
    setTableData(prev => ({
      ...prev,
      rows: newDataSource
    }));
  }, [canvasItems, rowBackgroundColors]);
  useEffect(() => {
    if (rowBackgroundColors) {
      setTableData(prev => ({
        ...prev,
        rows: prev.rows.map(row => {
          let id = '';
          if (typeof row.subject === 'object' && row.subject && 'id' in row.subject) {
            id = (row.subject as ExtendedSubject).id;
          } else if (typeof row.subject === 'string' && 'originalItem' in row && row.originalItem && typeof row.originalItem === 'object' && 'id' in row.originalItem) {
            id = (row.originalItem as ExtendedSubject).id;
          }
          return {
            ...row,
            backgroundColor: rowBackgroundColors.get(id) || "#fff"
          };
        })
      }));
    }
  }, [rowBackgroundColors]);
  const moveRow = (dragIndex: number, hoverIndex: number) => {
    if (readonly) return; 
    const dragRow = data[dragIndex];
    const newData = update(data, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragRow],
      ],
    });
    
    // 更新移动后的每一行的序号
    newData.forEach((row, index) => {
      row.index = index + 1;
    });
    
    setData(newData);
    
    const dragTableRow = tableData.rows[dragIndex];
    const newTableRows = update(tableData.rows, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragTableRow],
      ],
    });
    
    // 同样更新tableData中的序号
    newTableRows.forEach((row, index) => {
      row.index = index + 1;
    });
    
    const updatedTableData = {
      ...tableData,
      rows: newTableRows
    };
    
    setTableData(updatedTableData);
    
    // 通知父组件tableData已更新
    if (onTableDataChange) {
      onTableDataChange(updatedTableData);
    }
  };
  return (
    <div style={{ width: '100%' }}>
      <Table<KPIRow | NormalRow>
        bordered
        columns={columns}
        dataSource={data}
        pagination={false}
        style={{ width: '100%' }}
        locale={{ 
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="拖拽科目到此处添加数据" /> 
        }}
        className="template-table"
        components={{
          body: {
            row: DraggableRow,
          },
        }}
        onRow={(record, index) => {
          const idx = index ?? 0;
          const rowColor = record.backgroundColor || "#fff";
          let subjectId: string = '';
          if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
            subjectId = (record.subject as ExtendedSubject).id;
          } else if (typeof record.subject === 'string' && 'originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
            subjectId = (record.originalItem as ExtendedSubject).id;
          }
          return {
            index: idx,
            moveRow,
            className: selectedRowId === subjectId ? 'selected-row' : '',
            style: { backgroundColor: rowColor },
            onClick: () => {
              if (onRowSelect) {
                onRowSelect(subjectId);
              }
            },
            readonly
          };
        }}
        rowKey={(record) => {
          if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
            return String((record.subject as ExtendedSubject).id);
          }
          if (typeof record.subject === 'string' && 'originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
            return String((record.originalItem as ExtendedSubject).id);
          }
          return String(record.index);
        }}
        key="yearly-table"
      />
    </div>
  );
};
export const QuarterlyPlanTable = (props: {
  canvasItems: ExtendedSubject[];
  selectedRowId?: string | null;
  rowBackgroundColors?: Map<string, string>;
  onRowSelect?: (rowId: string) => void;
  readonly?: boolean;
  columnSettings?: ColumnSetting[];
  onRemoveItem?: (itemId: string) => void;
  onTableDataChange?: (data: TableData) => void;
}) => {
  const { canvasItems, selectedRowId, rowBackgroundColors, onRowSelect, readonly = false, columnSettings, onRemoveItem, onTableDataChange } = props;
  
  const initialData = createNewTableRows(canvasItems || [], rowBackgroundColors);
  
  // 状态
  const [data, setData] = useState<(KPIRow | NormalRow)[]>(initialData);
  const [tableData, setTableData] = useState<TableData>({
    header: createTableHeaderFromSettings(canvasItems, columnSettings),
    rows: initialData
  });

  // 生成列配置
  const columns = useMemo(() => {
    // 获取表头
    const tableHeader = createTableHeaderFromSettings(canvasItems, columnSettings);
    // 转换为表格列配置
    const baseColumns = generateSharedAntdColumns(tableHeader);
    
    // 添加操作列 - 仅当非只读模式且提供了删除回调时显示
    if (!readonly && onRemoveItem) {
      baseColumns.push({
        title: '操作',
        dataIndex: 'action',
        key: 'action',
        width: 60,
        render: (_: unknown, record: NormalRow | KPIRow) => (
          <Button
            type="text"
            danger
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onRemoveItem) onRemoveItem((record as KPIRow).originalItem?.id ?? (record as NormalRow).subject?.id);
            }}
            icon={<DeleteOutlined />}
          />
        ),
      });
    }
    
    return baseColumns;
  }, [canvasItems, columnSettings, onRemoveItem, readonly]);

  useEffect(() => {
    const newDataSource = createNewTableRows(canvasItems || [], rowBackgroundColors);
    setData(newDataSource);
    setTableData(prev => ({
      ...prev,
      rows: newDataSource
    }));
  }, [canvasItems, rowBackgroundColors]);
  useEffect(() => {
    if (rowBackgroundColors) {
      setTableData(prev => ({
        ...prev,
        rows: prev.rows.map(row => {
          let id = '';
          if (typeof row.subject === 'object' && row.subject && 'id' in row.subject) {
            id = (row.subject as ExtendedSubject).id;
          } else if (typeof row.subject === 'string' && 'originalItem' in row && row.originalItem && typeof row.originalItem === 'object' && 'id' in row.originalItem) {
            id = (row.originalItem as ExtendedSubject).id;
          }
          return {
            ...row,
            backgroundColor: rowBackgroundColors.get(id) || "#fff"
          };
        })
      }));
    }
  }, [rowBackgroundColors]);
  const moveRow = (dragIndex: number, hoverIndex: number) => {
    if (readonly) return; 
    const dragRow = data[dragIndex];
    const newData = update(data, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragRow],
      ],
    });
    
    // 更新移动后的每一行的序号
    newData.forEach((row, index) => {
      row.index = index + 1;
    });
    
    setData(newData);
    
    const dragTableRow = tableData.rows[dragIndex];
    const newTableRows = update(tableData.rows, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragTableRow],
      ],
    });
    
    // 同样更新tableData中的序号
    newTableRows.forEach((row, index) => {
      row.index = index + 1;
    });
    
    const updatedTableData = {
      ...tableData,
      rows: newTableRows
    };
    
    setTableData(updatedTableData);
    
    // 通知父组件tableData已更新
    if (onTableDataChange) {
      onTableDataChange(updatedTableData);
    }
  };
  return (
    <div style={{ width: '100%' }}>
      <Table<KPIRow | NormalRow>
        bordered
        columns={columns}
        dataSource={data}
        pagination={false}
        style={{ width: '100%' }}
        locale={{ 
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="拖拽科目到此处添加数据" /> 
        }}
        className="template-table"
        components={{
          body: {
            row: DraggableRow,
          },
        }}
        onRow={(record, index) => {
          const idx = index ?? 0;
          const rowColor = record.backgroundColor || "#fff";
          let subjectId: string = '';
          if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
            subjectId = (record.subject as ExtendedSubject).id;
          } else if (typeof record.subject === 'string' && 'originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
            subjectId = (record.originalItem as ExtendedSubject).id;
          }
          return {
            index: idx,
            moveRow,
            className: selectedRowId === subjectId ? 'selected-row' : '',
            style: { backgroundColor: rowColor },
            onClick: () => {
              if (onRowSelect) {
                onRowSelect(subjectId);
              }
            },
            readonly
          };
        }}
        rowKey={(record) => {
          if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
            return String((record.subject as ExtendedSubject).id);
          }
          if (typeof record.subject === 'string' && 'originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
            return String((record.originalItem as ExtendedSubject).id);
          }
          return String(record.index);
        }}
        key="quarterly-table"
      />
    </div>
  );
};
export const MonthlyPlanTable = (props: {
  canvasItems: ExtendedSubject[];
  selectedRowId?: string | null;
  rowBackgroundColors?: Map<string, string>;
  onRowSelect?: (rowId: string) => void;
  readonly?: boolean;
  isWeekly?: boolean; 
  isAmoeba?: boolean; 
  customColumns?: ColumnSetting[]; 
  columnSettings?: ColumnSetting[];
  onRemoveItem?: (itemId: string) => void;
  onTableDataChange?: (data: TableData) => void;
}) => {
  const { 
    canvasItems, 
    selectedRowId, 
    rowBackgroundColors, 
    onRowSelect,
    readonly,
    isWeekly = false,
    isAmoeba = false,
    customColumns,
    columnSettings,
    onRemoveItem,
    onTableDataChange
  } = props;
  
  const initialData = createNewTableRows(canvasItems || [], rowBackgroundColors);
  const [data, setData] = useState<(KPIRow | NormalRow)[]>(initialData);
  const [tableData, setTableData] = useState<TableData>({
    header: createTableHeaderFromSettings(canvasItems, columnSettings),
    rows: initialData
  });
  const generateColumns = useCallback(() => {
    let baseColumns;
    
    if (isWeekly && customColumns && customColumns.length > 0) {
      baseColumns = convertSettingsToColumns(customColumns);
    } else if (isAmoeba) {
      const tableHeader = createTableHeaderFromSettings(canvasItems || [], columnSettings);
      baseColumns = generateSharedAntdColumns(tableHeader);
    } else {
      const tableHeader = createTableHeaderFromSettings(canvasItems || [], columnSettings);
      baseColumns = generateSharedAntdColumns(tableHeader);
    }
    
    // 在非只读模式下添加操作列
    if (!readonly && onRemoveItem) {
      baseColumns.push({
        title: '操作',
        dataIndex: 'action',
        key: 'action',
        width: 60,
        render: (_: unknown, record: NormalRow | KPIRow) => (
          <Button
            type="text"
            danger
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onRemoveItem) onRemoveItem((record as KPIRow).originalItem?.id ?? (record as NormalRow).subject?.id);
            }}
            icon={<DeleteOutlined />}
          />
        ),
      });
    }
    
    return baseColumns;
  }, [isWeekly, isAmoeba, customColumns, canvasItems, columnSettings, readonly, onRemoveItem]);
  const convertSettingsToColumns = (settings: ColumnSetting[]) => {
    try {
      const weeklyColumns = settings.filter(col => 
        ['week1', 'week2', 'week3', 'week4', 'week5'].includes(col.key)
      );
      const yearlyPlanColumns = settings.filter(col => 
        col.key.startsWith('q') && ['q1', 'q2', 'q3', 'q4'].includes(col.key)
      );
      if (weeklyColumns.length > 0) {
        settings.forEach(col => {
          if (['week1', 'week2', 'week3', 'week4', 'week5'].includes(col.key)) {
            col.title = '月度计划(元)';
            if (!col.subTitle) {
              col.subTitle = `第${col.key.slice(-1)}周`;
            }
          }
        });
      }
      if (yearlyPlanColumns.length > 0) {
        settings.forEach(col => {
          if (['q1', 'q2', 'q3', 'q4'].includes(col.key)) {
            col.title = '年度计划(元)';
            if (!col.subTitle) {
              col.subTitle = col.key.toUpperCase();
            }
          }
        });
      }
      const groupedColumns: {[title: string]: ColumnSetting[]} = {};
      settings.forEach(col => {
        if (col.subTitle || 
            ['q1', 'q2', 'q3', 'q4', 'week1', 'week2', 'week3', 'week4', 'week5'].includes(col.key)) {
          if (!groupedColumns[col.title]) {
            groupedColumns[col.title] = [];
          }
          groupedColumns[col.title].push(col);
        }
      });
      const standaloneColumns = settings.filter(col => 
        !Object.keys(groupedColumns).some(title => 
          title === col.title && groupedColumns[title].some(groupCol => 
            groupCol.key === col.key
          )
        )
      );
      const baseColumns = [
        { 
          title: '序号', 
          dataIndex: 'index', 
          key: 'index', 
          width: 80,
          render: (value: unknown) => (<div className="first-column-cell">{String(value)}</div>)
        },
        { 
          title: '考核维度', 
          dataIndex: 'dimension', 
          key: 'dimension', 
          width: 120
        },
        { title: '一级科目', dataIndex: 'subject1', key: 'subject1', width: 120 },
        { title: '二级科目', dataIndex: 'subject2', key: 'subject2', width: 120 },
        { title: '三级科目', dataIndex: 'subject3', key: 'subject3', width: 120 },
        { title: '往期实际', dataIndex: 'previousActual', key: 'previousActual', width: 120 },
        { title: '本期目标', dataIndex: 'currentTarget', key: 'currentTarget', width: 120 },
        { title: '对比差距', dataIndex: 'comparison', key: 'comparison', width: 100 }
      ];
      const groupColumns = Object.entries(groupedColumns).map(([title, cols]) => ({
        title,
        children: cols.map(col => ({
          title: col.subTitle ?? null,
          dataIndex: col.key,
          key: col.key,
          width: col.width || 100,
        }))
      }));
      const regularColumns = standaloneColumns.map(col => {
        const column = {
          title: col.title || col.key,
          dataIndex: col.key,
          key: col.key,
          width: col.width || 100,
        };
        if (col.key === 'responsible') {
          return {
            ...column,
            render: (_value: unknown, record: NormalRow | KPIRow, _index?: number) => {
              const value = (record as Record<string, unknown>)[col.key];
              return <span>{typeof value === 'string' && value ? String(value) : '--'}</span>;
            }
          };
        }
        return column;
      });
      const achievementColumn = regularColumns.find(col => col.key === 'achievement');
      const achievementIndex = achievementColumn ? regularColumns.indexOf(achievementColumn) : -1;
      const yearlyPlanColumn = groupColumns.find(col => col.title === '年度计划(元)');
      const monthlyPlanColumn = groupColumns.find(col => col.title === '月度计划(元)');
      const filteredGroupColumns = groupColumns.filter(col => 
        col.title !== '年度计划(元)' && col.title !== '月度计划(元)'
      );
      const columnsBeforeAchievement = [...baseColumns, ...filteredGroupColumns, ...regularColumns.slice(0, achievementIndex)];
      const columnsAfterAchievement = achievementIndex >= 0 ? [...regularColumns.slice(achievementIndex)] : [...regularColumns];
      const finalColumns = [...columnsBeforeAchievement];
      const differenceIndex = finalColumns.findIndex(col => 
        'dataIndex' in col && col.dataIndex === 'comparison'
      );
      if (differenceIndex !== -1) {
        const planColumns = [];
        if (yearlyPlanColumn) planColumns.push(yearlyPlanColumn);
        if (monthlyPlanColumn) planColumns.push(monthlyPlanColumn);
        if (planColumns.length > 0) {
          finalColumns.splice(differenceIndex + 1, 0, ...planColumns);
        }
      } else if (achievementIndex !== -1) {
        const planColumns = [];
        if (yearlyPlanColumn) planColumns.push(yearlyPlanColumn);
        if (monthlyPlanColumn) planColumns.push(monthlyPlanColumn);
        if (planColumns.length > 0) {
          finalColumns.splice(finalColumns.length, 0, ...planColumns);
        }
      } else {
        if (yearlyPlanColumn) finalColumns.push(yearlyPlanColumn);
        if (monthlyPlanColumn) finalColumns.push(monthlyPlanColumn);
      }
      finalColumns.push(...columnsAfterAchievement);
      
      return finalColumns;
    } catch (error) {
          // 错误处理
      return [];
    }
  };
  useEffect(() => {
    const newDataSource = createNewTableRows(canvasItems || [], rowBackgroundColors);
    setData(newDataSource);
    setTableData(prev => ({
      ...prev,
      rows: newDataSource
    }));
  }, [canvasItems, rowBackgroundColors]);
  useEffect(() => {
    if (rowBackgroundColors) {
      setTableData(prev => ({
        ...prev,
        rows: prev.rows.map(row => {
          let id = '';
          if (typeof row.subject === 'object' && row.subject && 'id' in row.subject) {
            id = (row.subject as ExtendedSubject).id;
          } else if (typeof row.subject === 'string' && 'originalItem' in row && row.originalItem && typeof row.originalItem === 'object' && 'id' in row.originalItem) {
            id = (row.originalItem as ExtendedSubject).id;
          }
          return {
            ...row,
            backgroundColor: rowBackgroundColors.get(id) || "#fff"
          };
        })
      }));
    }
  }, [rowBackgroundColors]);
  const moveRow = (dragIndex: number, hoverIndex: number) => {
    if (readonly) return; 
    const dragRow = data[dragIndex];
    const newData = update(data, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragRow],
      ],
    });
    
    // 更新移动后的每一行的序号
    newData.forEach((row, index) => {
      row.index = index + 1;
    });
    
    setData(newData);
    
    const dragTableRow = tableData.rows[dragIndex];
    const newTableRows = update(tableData.rows, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragTableRow],
      ],
    });
    
    // 同样更新tableData中的序号
    newTableRows.forEach((row, index) => {
      row.index = index + 1;
    });
    
    const updatedTableData = {
      ...tableData,
      rows: newTableRows
    };
    
    setTableData(updatedTableData);
    
    // 通知父组件tableData已更新
    if (onTableDataChange) {
      onTableDataChange(updatedTableData);
    }
  };
  const columns = generateColumns();
  return (
    <div style={{ width: '100%' }}>
      <Table<KPIRow | NormalRow>
        bordered
        columns={columns}
        dataSource={data}
        pagination={false}
        style={{ width: '100%' }}
        locale={{ 
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="拖拽科目到此处添加数据" /> 
        }}
        className="template-table"
        components={{
          body: {
            row: DraggableRow,
          },
        }}
        onRow={(record, index) => {
          const idx = index ?? 0;
          const rowColor = record.backgroundColor || "#fff";
          let subjectId: string = '';
          if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
            subjectId = (record.subject as ExtendedSubject).id;
          } else if (typeof record.subject === 'string' && 'originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
            subjectId = (record.originalItem as ExtendedSubject).id;
          }
          return {
            index: idx,
            moveRow,
            className: selectedRowId === subjectId ? 'selected-row' : '',
            style: { backgroundColor: rowColor },
            onClick: () => {
              if (onRowSelect) {
                onRowSelect(subjectId);
              }
            },
            readonly
          };
        }}
        rowKey={(record) => {
          if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
            return String((record.subject as ExtendedSubject).id);
          }
          if (typeof record.subject === 'string' && 'originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
            return String((record.originalItem as ExtendedSubject).id);
          }
          return String(record.index);
        }}
        key="monthly-table"
      />
    </div>
  );
};
export const getTemplateByType = (
  type: string,
  canvasItems: ExtendedSubject[] = [],
  selectedRowId?: string | null,
  rowBackgroundColors?: Map<string, string>,
  onRowSelect?: (rowId: string) => void,
  columnSettings?: ColumnSetting[],
  kpiOnRemoveItem?: (itemId: string) => void,
  readonly?: boolean,
  onTableDataChange?: (data: TableData) => void,
  frequencyType?: string,
  reportType?: string,
  allSubjects?: ExtendedSubject[]
) => {
  // 调试：输出函数参数
  if (frequencyType === 'Monthly' && reportType === 'KPI') {
    const rows = createNewTableRows(canvasItems, rowBackgroundColors, 'kpi', allSubjects);
    // 生成AntD专用columns，包含操作列
    const kpiColumns: ColumnType<KPIRow | NormalRow>[] = [
      ...KPI_HEADER.map(col => ({
        ...col,
        dataIndex: col.dataIndex,
        key: col.key,
        title: col.title,
        width: col.width || 120,
      })),
    ];
    
    // 如果不是只读模式且提供了删除回调，添加操作列
    if (!readonly && kpiOnRemoveItem) {
      kpiColumns.push({
        key: 'action',
        title: '操作',
        dataIndex: 'action',
        width: 48,
        render: (_: unknown, record: KPIRow | NormalRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              if (kpiOnRemoveItem) kpiOnRemoveItem((record as KPIRow).originalItem?.id ?? (record as NormalRow).subject?.id);
            }}
            style={{ padding: 0 }}
            title="删除"
          />
        ),
      });
    }
    
    return (
      <div style={{ width: '100%' }}>
        <Table<KPIRow | NormalRow>
          bordered
          columns={kpiColumns}
          dataSource={rows}
          pagination={false}
          style={{ width: '100%' }}
          size="small"
          locale={{ 
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="拖拽科目到此处添加数据" /> 
          }}
          className="template-table kpi-table"
          components={{
            body: {
              row: DraggableRow,
            },
          }}
          onRow={(record: KPIRow | NormalRow, index) => {
            const idx = index ?? 0;
            const rowColor = record.backgroundColor || "#fff";
            let subjectId: string = '';
            
            // 对于KPI行，直接从originalItem获取ID
            if ('originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
              subjectId = (record.originalItem as ExtendedSubject).id;
            } else if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
              subjectId = (record.subject as ExtendedSubject).id;
            }
            
            return {
              index: idx,
              className: selectedRowId === subjectId ? 'selected-row' : '',
              style: { backgroundColor: rowColor },
              onClick: () => {
                if (onRowSelect && !readonly) {
                  onRowSelect(subjectId);
                }
              },
            };
          }}
          rowKey={(record: KPIRow | NormalRow) => {
            // 对于KPI行，直接从originalItem获取ID
            if ('originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
              return String((record.originalItem as ExtendedSubject).id);
            }
            if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
              return String((record.subject as ExtendedSubject).id);
            }
            return String(record.index);
          }}
          scroll={{ x: 'max-content' }}
        />
      </div>
    );
  }
  const lowerType = type.toLowerCase();
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const isUuid = uuidPattern.test(type);
  let effectiveType = lowerType;
  if (isUuid) {
    const activeSettings = columnSettings && columnSettings.length > 0 
      ? columnSettings 
      : currentColumnSettings;
    const hasYearlyColumns = activeSettings.some((col: ColumnSetting) => 
      col.key === 'q1' || col.key === 'q2' || col.key === 'q3' || col.key === 'q4'
    );
    if (hasYearlyColumns) {
      effectiveType = 'yearly';
    } else {
      effectiveType = 'monthly';
    }
  }
  let weeklyColumnSettings = columnSettings;
  if (effectiveType === 'weekly') {
    if (columnSettings && columnSettings.length > 0) {
      weeklyColumnSettings = columnSettings;
    } else if (currentColumnSettings.length > 0) {
      weeklyColumnSettings = currentColumnSettings;
    } else {
      weeklyColumnSettings = undefined;
    }
  }
  switch (effectiveType) {
    case 'yearly': {
      return (
        <YearlyPlanTable 
          canvasItems={canvasItems} 
          selectedRowId={selectedRowId}
          rowBackgroundColors={rowBackgroundColors}
          onRowSelect={onRowSelect}
          columnSettings={columnSettings}
          onRemoveItem={kpiOnRemoveItem}
          readonly={readonly}
          onTableDataChange={onTableDataChange}
        />
      );
    }
    case 'quarterly': {
      return (
        <QuarterlyPlanTable 
          canvasItems={canvasItems} 
          selectedRowId={selectedRowId}
          rowBackgroundColors={rowBackgroundColors}
          onRowSelect={onRowSelect}
          columnSettings={columnSettings}
          onRemoveItem={kpiOnRemoveItem}
          readonly={readonly}
          onTableDataChange={onTableDataChange}
        />
      );
    }
    case 'monthly': {
      return (
        <MonthlyPlanTable 
          canvasItems={canvasItems} 
          selectedRowId={selectedRowId}
          rowBackgroundColors={rowBackgroundColors}
          onRowSelect={onRowSelect}
          columnSettings={columnSettings}
          onRemoveItem={kpiOnRemoveItem}
          readonly={readonly}
          onTableDataChange={onTableDataChange}
        />
      );
    }
    case 'weekly': {
      return (
        <MonthlyPlanTable 
          canvasItems={canvasItems} 
          selectedRowId={selectedRowId}
          rowBackgroundColors={rowBackgroundColors}
          onRowSelect={onRowSelect}
          isWeekly
          columnSettings={weeklyColumnSettings}
          onRemoveItem={kpiOnRemoveItem}
          readonly={readonly}
          onTableDataChange={onTableDataChange}
        />
      );
    }
    case 'amoeba': {
      return (
        <MonthlyPlanTable 
          canvasItems={canvasItems} 
          selectedRowId={selectedRowId}
          rowBackgroundColors={rowBackgroundColors}
          onRowSelect={onRowSelect}
          isAmoeba
          columnSettings={columnSettings}
          onRemoveItem={kpiOnRemoveItem}
          readonly={readonly}
          onTableDataChange={onTableDataChange}
        />
      );
    }
    case 'kpi': {
      const rows = createNewTableRows(canvasItems, rowBackgroundColors, 'kpi', allSubjects);
      
      // 生成AntD专用columns，包含操作列
      const kpiColumns: ColumnType<KPIRow | NormalRow>[] = [
        ...KPI_HEADER.map(col => ({
          ...col,
          dataIndex: col.dataIndex,
          key: col.key,
          title: col.title,
          width: col.width || 120,
        })),
      ];
      
      // 如果不是只读模式且提供了删除回调，添加操作列
      if (!readonly && kpiOnRemoveItem) {
        kpiColumns.push({
          key: 'action',
          title: '操作',
          dataIndex: 'action',
          width: 48,
          render: (_: unknown, record: KPIRow | NormalRow) => (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                if (kpiOnRemoveItem) kpiOnRemoveItem((record as KPIRow).originalItem?.id ?? (record as NormalRow).subject?.id);
              }}
              style={{ padding: 0 }}
              title="删除"
            />
          ),
        });
      }
      
      return (
        <div style={{ width: '100%' }}>
          <Table<KPIRow | NormalRow>
            bordered
            columns={kpiColumns}
            dataSource={rows}
            pagination={false}
            style={{ width: '100%' }}
            size="small"
            locale={{ 
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="拖拽科目到此处添加数据" /> 
            }}
            className="template-table kpi-table"
            components={{
              body: {
                row: DraggableRow,
              },
            }}
            onRow={(record: KPIRow | NormalRow, index) => {
              const idx = index ?? 0;
              const rowColor = record.backgroundColor || "#fff";
              let subjectId: string = '';
              
              // 对于KPI行，直接从originalItem获取ID
              if ('originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
                subjectId = (record.originalItem as ExtendedSubject).id;
              } else if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
                subjectId = (record.subject as ExtendedSubject).id;
              }
              
              return {
                index: idx,
                className: selectedRowId === subjectId ? 'selected-row' : '',
                style: { backgroundColor: rowColor },
                onClick: () => {
                  if (onRowSelect && !readonly) {
                    onRowSelect(subjectId);
                  }
                },
              };
            }}
            rowKey={(record: KPIRow | NormalRow) => {
              // 对于KPI行，直接从originalItem获取ID
              if ('originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
                return String((record.originalItem as ExtendedSubject).id);
              }
              if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
                return String((record.subject as ExtendedSubject).id);
              }
              return String(record.index);
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      );
    }
    default: {
      return (
        <MonthlyPlanTable 
          canvasItems={canvasItems} 
          selectedRowId={selectedRowId}
          rowBackgroundColors={rowBackgroundColors}
          onRowSelect={onRowSelect}
          columnSettings={columnSettings}
          onRemoveItem={kpiOnRemoveItem}
          readonly={readonly}
          onTableDataChange={onTableDataChange}
        />
      );
    }
  }
};
export const getTemplateData = (
  templateType: string, 
  items: ExtendedSubject[] = [],
  rowBackgroundColors?: Map<string, string>,
  columnSettings?: ColumnSetting[]
): TableData => {
  if (templateType && templateType.toLowerCase() === 'kpi') {
    const rows: KPIRow[] = items.map((item, index) => {
      // 生成完整的科目显示名称，包含父级科目和代码
      let subjectDisplayName = item.name;
      
      // 添加父级科目信息
      if (item.parentName && item.grandParentName) {
        subjectDisplayName = `${item.grandParentName}/${item.parentName}/${item.name}`;
      } else if (item.parentName) {
        subjectDisplayName = `${item.parentName}/${item.name}`;
      }
      
      // 添加代码信息
      if (item.code) {
        subjectDisplayName = `${subjectDisplayName} (${item.code})`;
      }
      
      return {
        index: index + 1,
        subject: subjectDisplayName,
        dynamicMonths: '-',
        nextMonth: '-',
        sum: '-',
        sumDiff: '-',
        sumRate: '-',
        backgroundColor: rowBackgroundColors?.get(item.id) || '#fff',
        originalItem: item,
      };
    });
    return {
      header: KPI_HEADER.map(setting => ({
        key: setting.key,
        title: setting.title,
        subTitle: null,
      })),
      rows,
    };
  }
  const tableHeader = createTableHeaderFromSettings(items, columnSettings, templateType);
  const rows = createNewTableRows(items, rowBackgroundColors, templateType);
  return {
    header: tableHeader,
    rows
  };
};
export const QuarterReportTable = (props: TableProps) => {
  const { canvasItems, selectedRowId, rowBackgroundColors, onRowSelect, readonly } = props;
};
export const MonthReportTable = (props: TableProps) => {
  const { canvasItems, selectedRowId, rowBackgroundColors, onRowSelect, readonly } = props;
};
export const YearReportTable = (props: TableProps) => {
  const { canvasItems, selectedRowId, rowBackgroundColors, onRowSelect, readonly } = props;
};
export const AmoebaTable = React.memo((props: TableProps) => {
  const { canvasItems, selectedRowId, rowBackgroundColors, onRowSelect, readonly, columnSettings, onTableDataChange } = props;
  const initialData = createNewTableRows(canvasItems || [], rowBackgroundColors);
  const [data, setData] = useState<(KPIRow | NormalRow)[]>(initialData);
  const [tableData, setTableData] = useState<TableData>({
    header: createTableHeaderFromSettings(canvasItems || [], columnSettings),
    rows: initialData
  });
  useEffect(() => {
    const newDataSource = createNewTableRows(canvasItems || [], rowBackgroundColors);
    setData(newDataSource);
    setTableData(prev => ({
      header: createTableHeaderFromSettings(canvasItems || [], columnSettings),
      rows: newDataSource
    }));
  }, [canvasItems, rowBackgroundColors, columnSettings]);
  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (readonly) return; 
      const dragRow = data[dragIndex];
      const newData = update(data, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      });
      
      // 更新移动后的每一行的序号
      newData.forEach((row, index) => {
        row.index = index + 1;
      });
      
      setData(newData);
      
      const dragTableRow = tableData.rows[dragIndex];
      const newTableRows = update(tableData.rows, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragTableRow],
        ],
      });
      
      // 同样更新tableData中的序号
      newTableRows.forEach((row, index) => {
        row.index = index + 1;
      });
      
      const updatedTableData = {
        ...tableData,
        rows: newTableRows
      };
      
      setTableData(updatedTableData);
      
      // 通知父组件tableData已更新
      if (onTableDataChange) {
        onTableDataChange(updatedTableData);
      }
    },
    [data, tableData, readonly, onTableDataChange]
  );
  const columns = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 60,
    },
    {
      title: '一级科目',
      dataIndex: 'subject1',
      key: 'subject1',
      width: 120,
    },
    {
      title: '二级科目',
      dataIndex: 'subject2',
      key: 'subject2',
      width: 120,
    },
    {
      title: '三级科目',
      dataIndex: 'subject3',
      key: 'subject3',
      width: 150,
    },
    {
      title: '计划',
      children: [
        {
          title: '金额(元)',
          dataIndex: 'currentTarget',
          key: 'currentTarget',
          width: 100,
        },
        {
          title: '本巴占比(%)',
          dataIndex: 'currentRatio1',
          key: 'currentRatio1',
          width: 100,
        },
        {
          title: '公司占比(%)',
          dataIndex: 'currentRatio2',
          key: 'currentRatio2',
          width: 100,
        },
      ],
    },
    {
      title: '实际',
      children: [
        {
          title: '金额(元)',
          dataIndex: 'achievement',
          key: 'achievement',
          width: 100,
        },
        {
          title: '本巴占比(%)',
          dataIndex: 'achievementRatio1',
          key: 'achievementRatio1',
          width: 100,
        },
        {
          title: '公司占比(%)',
          dataIndex: 'achievementRatio2',
          key: 'achievementRatio2',
          width: 100,
        },
      ],
    },
    {
      title: '差异',
      children: [
        {
          title: '差异比',
          dataIndex: 'comparisonRatio',
          key: 'comparisonRatio',
          width: 100,
        },
        {
          title: '差异金额(元)',
          dataIndex: 'comparison',
          key: 'comparison',
          width: 100,
        },
        {
          title: '差异趋势',
          dataIndex: 'comparisonTrend',
          key: 'comparisonTrend',
          width: 100,
        },
      ],
    },
  ];
  return (
    <div style={{ width: '100%' }}>
      <Table<KPIRow | NormalRow>
        bordered
        columns={columns}
        dataSource={data}
        pagination={false}
        style={{ width: '100%' }}
        size="small"
        locale={{ 
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="拖拽科目到此处添加数据" /> 
        }}
        className="template-table amoeba-table"
        components={{
          body: {
            row: DraggableRow,
          },
        }}
        onRow={(record, index) => {
          const idx = index ?? 0;
          const rowColor = record.backgroundColor || "#fff";
          let subjectId: string = '';
          if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
            subjectId = (record.subject as ExtendedSubject).id;
          } else if (typeof record.subject === 'string' && 'originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
            subjectId = (record.originalItem as ExtendedSubject).id;
          }
          return {
            index: idx,
            moveRow,
            className: selectedRowId === subjectId ? 'selected-row' : '',
            style: { backgroundColor: rowColor },
            onClick: () => {
              if (onRowSelect && !readonly) {
                onRowSelect(subjectId);
              }
            },
            readonly
          };
        }}
        rowKey={(record) => {
          if (typeof record.subject === 'object' && record.subject && 'id' in record.subject) {
            return String((record.subject as ExtendedSubject).id);
          }
          if (typeof record.subject === 'string' && 'originalItem' in record && record.originalItem && typeof record.originalItem === 'object' && 'id' in record.originalItem) {
            return String((record.originalItem as ExtendedSubject).id);
          }
          return String(record.index);
        }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
});
